<template>
  <div class="component-upload-image">
    <el-upload
      multiple
      :action="uploadImgUrl"
      list-type="picture-card"
      :on-success="handleUploadSuccess"
      :name="name"
      :before-upload="handleBeforeUpload"
      :limit="limit"
      :on-error="handleUploadError"
      :on-exceed="handleExceed"
      ref="imageUpload"
      :on-remove="handleDelete"
      :show-file-list="true"
      :file-list="fileList"
      :on-preview="handlePictureCardPreview"
      :class="{ hide: fileList.length >= limit }"
      :headers="headers"
      :data="params"
    >
      <el-icon><Plus /></el-icon>
    </el-upload>

    <!-- 上传提示 -->
    <div class="el-upload__tip" v-if="showTip">
      请上传
      <template v-if="fileSize">
        大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
      </template>
      <template v-if="fileType">
        格式为 <b style="color: #f56c6c">{{ fileType.join("/") }}</b>
      </template>
      的文件
    </div>

    <!-- 预览 -->
    <el-dialog :model-value="dialogVisible" title="预览" width="800" append-to-body @close="dialogVisible = false">
      <img :src="dialogImageUrl" style="display: block; max-width: 100%; margin: 0 auto" />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { SYS_BASE_URL, PICTURE_URL } from "@/constant/Const";
import { ElMessage } from "element-plus";

// Props
const props = defineProps({
  modelValue: [String, Object, Array],
  limit: { type: Number, default: 5 },
  fileSize: { type: Number, default: 5 },
  fileType: { type: Array, default: () => ["png", "jpg", "jpeg"] },
  name: { type: String, default: "file" },
  isShowTip: { type: Boolean, default: true },
  params: { type: Object, default: () => ({}) },
  url: { type: String, default: "/api/img/uploadSign" },
});

// Emits
const emit = defineEmits(["update:modelValue"]);

const fileList = ref([]);
const uploadList = ref([]);
const number = ref(0);
const dialogVisible = ref(false);
const dialogImageUrl = ref("");
const uploadImgUrl = ref(SYS_BASE_URL + "/api/img/uploadSign");
const headers = reactive({
  Authorization: sessionStorage.getItem("token"),
});
const baseUrl = ref(PICTURE_URL);

// 显示提示
const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize));

// 监听 modelValue 初始化 fileList
watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      const list = Array.isArray(val) ? val : String(val).split(",");
      fileList.value = list.map((item) => {
        if (typeof item === "string") {
        //   const url = item.startsWith(baseUrl.value) ? item : baseUrl.value + "/" + item;
          const url = item.startsWith(baseUrl.value) ? item : item;
          return { name: url, url };
        }
        return item;
      });
    } else {
      fileList.value = [];
    }
  },
  { deep: true, immediate: true }
);

// 监听 url 改变
watch(
  () => props.url,
  (val) => {
    if (val) uploadImgUrl.value = SYS_BASE_URL + val;
  },
  { immediate: true }
);

// 方法
function handleBeforeUpload(file) {
  const fileExtension = file.name.split(".").pop().toLowerCase();
  const isImg =
    props.fileType.includes(fileExtension) || file.type.indexOf("image") > -1;
  if (!isImg) {
    ElMessage.error(`文件格式不正确, 请上传${props.fileType.join("/")}图片格式文件!`);
    return false;
  }
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      ElMessage.error(`上传图片大小不能超过 ${props.fileSize} MB!`);
      return false;
    }
  }
  number.value++;
  return true;
}

function handleExceed() {
  ElMessage.error(`上传文件数量不能超过 ${props.limit} 个!`);
}

function handleUploadSuccess(res, file) {
  if (res.code === 1 || res.code === 0) {
    
    const url = `https://${res.data.domain}/${res.data.pathPrefix}${res.data.storageFilePath}${res.data.newFileName}`;
    console.log(url);
    uploadList.value.push({ name: url, url });
    uploadedSuccessfully();
  } else {
    number.value--;
    ElMessage.error(res.msg);
    uploadedSuccessfully();
  }
}

function handleUploadError() {
  ElMessage.error("上传图片失败，请重试");
}

function uploadedSuccessfully() {
  if (number.value > 0 && uploadList.value.length === number.value) {
    fileList.value = fileList.value.concat(uploadList.value);
    uploadList.value = [];
    number.value = 0;
    emit("update:modelValue", fileList.value.map((f) => f.url).join(","));
  }
}

function handleDelete(file) {
  const index = fileList.value.findIndex((f) => f.name === file.name);
  if (index > -1) {
    fileList.value.splice(index, 1);
    emit("update:modelValue", fileList.value.map((f) => f.url).join(","));
  }
}

function handlePictureCardPreview(file) {
  dialogImageUrl.value = file.url;
  dialogVisible.value = true;
}
</script>

<style scoped lang="scss">
::v-deep .hide .el-upload--picture-card {
  display: none;
}
::v-deep .el-list-enter-active,
::v-deep .el-list-leave-active {
  transition: all 0s;
}
::v-deep .el-list-enter,
.el-list-leave-active {
  opacity: 0;
  transform: translateY(0);
}
</style>
